import { Directive, Injector, OnDestroy, ViewChild } from '@angular/core';
import { PoDialogService, PoModalComponent, PoNotification, PoNotificationService } from '@po-ui/ng-components';
import { MingleService } from '@totvs/mingle';
import { Parameter } from '@totvs/mingle/src/models/parameter.model';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { User } from 'src/app/core/home/login/shared/user';
import { LiteralsWords } from 'src/app/i18n/literals.model';
import { SchemasService } from 'src/app/shared/services/schemas.service';
import { SellersService } from 'src/app/shared/services/sellers.service';
import { environment } from 'src/environments/environment';
import { AuthError } from '../../interfaces/login.model';
import { UserAccessRules } from '../../interfaces/user-access-rules.model';
import { LiteralsService } from '../../services/literals-service';
import { SetupSyncService } from '../../services/setup-sync.service';
import { UserService } from '../../services/user.service';
import { VersionService } from '../../services/version.service';
import { DeviceService } from './../../services/device.service';
import { UserAccessRulesService } from './../../services/user-access-rules/user-access-rules.service';

@Directive()
export abstract class BaseLoginComponent implements OnDestroy {
  literals: LiteralsWords;
  version$ = new Subscription();
  @ViewChild(PoModalComponent, { static: true }) poModal: PoModalComponent;
  errorOrigin: string;
  errorAuth: string;
  errorTitle: string;
  poNotificationError: PoNotification;

  protected poDialog: PoDialogService;
  protected literalsService: LiteralsService;
  protected mingleService: MingleService;
  protected poNotificationService: PoNotificationService;
  protected setupSyncService: SetupSyncService;
  protected userService: UserService;
  protected versionService: VersionService;
  protected sellerService: SellersService;
  protected schemasService: SchemasService;
  protected userAccessRulesService: UserAccessRulesService;
  protected deviceService: DeviceService;

  ngOnDestroy(): void {
    this.version$.unsubscribe();
  }

  constructor(protected injector: Injector) {
    this.poDialog = injector.get(PoDialogService);
    this.literalsService = injector.get(LiteralsService);
    this.mingleService = injector.get(MingleService);
    this.poNotificationService = injector.get(PoNotificationService);
    this.setupSyncService = injector.get(SetupSyncService);
    this.versionService = injector.get(VersionService);
    this.userService = injector.get(UserService);
    this.sellerService = injector.get(SellersService);
    this.schemasService = injector.get(SchemasService);
    this.userAccessRulesService = injector.get(UserAccessRulesService);
    this.deviceService = injector.get(DeviceService);
    this.literals = this.literalsService.getLiteralModule();
  }

  onUserAuth(user: User): void {
    this.onMingleAuth(user);
  }

  onMingleAuth(user: User): void {
    this.mingleService.auth.login(user.login, user.password, user.alias).subscribe(
      res => {
        const companyMingle = this.getCompanyFromMingle(res.params_values);
        const branchMingle = this.getBranchFromMingle(res.params_values);

        if (!companyMingle || !branchMingle) {
          this.poNotificationError = {
            message: this.literals.str0676,
            action: () => this.poModal.open(),
            actionLabel: this.literals.str0061
          };
          this.errorOrigin = 'Mingle';
          this.errorAuth = this.literals.str0677;
          this.errorTitle = this.literals.str0678;
          this.poNotificationService.error(this.poNotificationError);
          this.setCancelLoading();
        } else {
          this.checkSchemas(user);
        }
      },
      authError => {
        this.setCancelLoading();
        this.checkLoginError(authError);
      }
    );
  }

  getCompanyFromMingle(param: Parameter[]): string {
    return param.filter(e => e.key.toUpperCase() === 'EMPRESA').map(e => e.value)[0];
  }

  getBranchFromMingle(param: Parameter[]): string {
    return param.filter(e => e.key.toUpperCase() === 'FILIAL').map(e => e.value)[0];
  }

  checkLoginError(authError: AuthError): void {
    const responseData = authError.response.data;
    const isFromMingle = responseData.origin === 'mingle';
    this.errorOrigin = isFromMingle ? 'Mingle' : 'TOTVS Backoffice Protheus';
    this.errorAuth = responseData.message;
    this.errorTitle = isFromMingle
      ? this.handleMingleError(this.errorAuth)
      : this.handleProtheusError(responseData.error);
    this.poNotificationError = {
      message: this.errorTitle,
      action: () => this.poModal.open(),
      actionLabel: this.literals.str0061
    };
    this.poNotificationService.error(this.poNotificationError);
  }

  handleMingleError(msg: string): string {
    return msg === 'Client not found' ? this.literals.str0646 : msg;
  }

  handleProtheusError(msg: string): string {
    if (msg === 'Unauthorized') {
      return this.literals.str0648;
    } else if (msg === 'Service Unavailable') {
      return this.literals.str0653;
    }
    return msg;
  }

  checkSchemas(user: User): void {
    this.schemasService.hasSchemas().subscribe({
      next: async res => {
        if (res) {
          localStorage.setItem('ALIAS', user.alias);
          await this.checkVersion(user);
        } else {
          this.setCancelLoading();
          this.poDialog.alert({
            title: this.literals.str0506,
            message: this.literals.str0500
          });
        }
      },
      error: error => {
        this.poNotificationService.error(error);
        this.setCancelLoading();
      }
    });
  }

  async checkVersion(user: User): Promise<void> {
    this.version$ = this.versionService.get().subscribe({
      next: res => {
        localStorage.setItem('serverVersion', res.version);
        if (this.isValidVersion(res)) {
          this.checkIfCanSellerAccess(user);
        }
      },
      error: error => {
        if (error.status === 404) {
          this.setCancelLoading();
          this.poDialog.alert({
            title: this.literals.str0533,
            message: this.literals.str0532
          });
        } else {
          this.setCancelLoading();
          this.poDialog.alert({
            title: this.literals.str0564,
            message: `${error.error.errorMessage} <br><br> ${this.literals.str0532}`
          });
        }
      }
    });
  }

  checkIfCanSellerAccess(user: User): void {
    this.sellerService
      .getSeller()
      .pipe(take(1))
      .subscribe({
        next: seller => {
          if (this.sellerService.canSellerAccess(seller.items[0])) {
            this.sellerService
              .setSellerToStorage(seller)
              .then(() => {
                this.checkExistUser(user);
              })
              .catch(error => {
                this.poNotificationService.error(`${this.literals.str0541}: ${error}`); // Não foi possível registrar o vendedor.
                this.setCancelLoading();
              });
          } else {
            this.setCancelLoading();
          }
        },
        error: error => {
          this.poNotificationService.error(`${this.literals.str0530}: ${error}`); // Falha ao consultar vendedor.
          this.setCancelLoading();
        }
      });
  }

  async checkExistUser(user: User): Promise<void> {
    await this.userService
      .existUser(user.login)
      .then(() => this.getUserAccessRules(user))
      .catch(() => {
        this.setCancelLoading();
      });
  }

  getUserAccessRules(user: User): void {
    this.userAccessRulesService.getUserAccessRules().subscribe({
      next: (rules: UserAccessRules) => {
        this.userAccessRulesService.setUserAccessRulesOnStorage(rules);
        this.setLoggedUserToStorageAndLogin(user);
        this.addDeviceMetricToMingle();
      },
      error: () => this.poNotificationService.error(`${this.literals.str0558}.`)
    });
  }

  isValidVersion(version: { version: string }): boolean {
    if (version.version < environment.pgvVersion && !this.hasVersionValid(version.version)) {
      this.setCancelLoading();
      this.poDialog.alert({
        title: this.literals.str0533,
        message: this.literals.str0532
      });
      return false;
    }
    return true;
  }

  hasVersionValid(version: string): boolean {
    const exceptionVersions = ['1.0.0', '1.1.0', '1.2.0', '1.3.0', '1.3.1', '1.4.1', '1.4.3', '1.4.8'];
    exceptionVersions.push('1.5.0');
    exceptionVersions.push('1.5.3');
    exceptionVersions.push('1.5.4');
    exceptionVersions.push('1.5.5');
    exceptionVersions.push('1.6.0');
    exceptionVersions.push('1.6.1');
    exceptionVersions.push('1.7.0');
    exceptionVersions.push('1.8.0');
    exceptionVersions.push('1.8.1');
    exceptionVersions.push('1.8.2');
    exceptionVersions.push('1.11.3');
    exceptionVersions.push('1.11.4');
    exceptionVersions.push('1.11.5');
    exceptionVersions.push('1.11.6');
    exceptionVersions.push('1.11.7');
    exceptionVersions.push('1.11.8');
    exceptionVersions.push('1.11.9');
    exceptionVersions.push('1.11.10');
    exceptionVersions.push('1.11.11');
    return exceptionVersions.findIndex(v => v === version) >= 0;
  }

  addDeviceMetricToMingle(): void {
    this.mingleService.registerMetric('PGV-DEVICE', this.getDeviceBeingUsed());
  }

  getDeviceBeingUsed(): object {
    if (this.deviceService.returnSmartphone()) {
      return { isMobile: true, device: 'Smartphone' };
    } else if (this.deviceService.returnTablet()) {
      return { isMobile: true, device: 'Tablet' };
    } else {
      return { isMobile: false, device: 'PC' };
    }
  }

  abstract setLoggedUserToStorageAndLogin(user: User): void;
  abstract setCancelLoading(): void;
}
